import moment from "moment-timezone";
import store from "../reduxstore/reduxStore";

import { MONTH_OFFSET } from "constants/AppConstants";

export const DATE_RANGES = {
  get Today() {
    return {
      start: moment().startOf("day"),
      end: moment().endOf("day")
    };
  },
  get Last30Days() {
    return {
      startDate: moment().subtract(MONTH_OFFSET, "days").startOf("day"),
      endDate: moment().endOf("day")
    };
  }
};

export const dateRanges = {
  Today: [DATE_RANGES.Today.start, DATE_RANGES.Today.end],
  Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
  "Last 7 Days": [moment().subtract(6, "days"), moment()],
  "Last 30 Days": [moment().subtract(MONTH_OFFSET, "days"), moment()],
  "This Month": [moment().startOf("month"), moment().endOf("month")],
  "Last Month": [
    moment().subtract(1, "month").startOf("month"),
    moment().subtract(1, "month").endOf("month")
  ]
};

export function toHoursAndMinutes(time) {
  let [hours, minutes] = time.toString().split(".");

  minutes = minutes ? Math.ceil(60 * parseFloat(`0.${minutes}`)) : 0;

  return [hours, minutes.toString()];
}

/**
 * @param  {date} time
 * @param  {number | string} division
 * @param  {string} format
 * @param  {boolean} isFormatRequired
 * @return {date} date of local time with/without format
 */
export function toLocalTime(time, division, format, isFormatRequired = false) {
  const selectedDivision = store
    .getState()
    .auth.user?.divisions?.find(e => e.warehouse.srm_uid === division?.trim());
  let timezone = "";
  if (selectedDivision) {
    timezone = selectedDivision.warehouse.timezone;
  }
  if (isFormatRequired) {
    return moment.tz(time, format, timezone).utc().local().format(format);
  }
  return moment.tz(time, format, timezone).utc().local();
}

function toWarehouseTime({
  time,
  division,
  format = "",
  warehouses,
  changeTimezoneOnly
}) {
  if (moment(time).isValid() === false) {
    return null;
  }
  const selectedDivision = warehouses.find(
    e => e.warehouse.srm_uid === division || e.warehouse.id === division
  );
  let timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (selectedDivision) {
    timezone = selectedDivision.warehouse.timezone;
  }
  if (changeTimezoneOnly) {
    return moment(time).parseZone().tz(timezone, true).format(format);
  }
  return moment(time).utc().tz(timezone).format(format);
}

/**
 * @param  {date} time
 * @param  {number | "" | string} division
 * @param  {string} format
 * @return {date} formated date of division's time
 */
export function toDivisionTime(
  time,
  division,
  format = "",
  changeTimezoneOnly = false
) {
  return toWarehouseTime({
    time,
    division,
    format,
    warehouses: store.getState().auth?.user?.divisions || [],
    changeTimezoneOnly
  });
}

/**
 * @param  {date} time
 * @param  {number | string} division
 * @param  {string} format
 * @return {date} formated date of division's time
 */
export function toLocationTime(
  time,
  division,
  format = "",
  changeTimezoneOnly = false
) {
  return toWarehouseTime({
    time,
    division,
    format,
    warehouses: store.getState().auth?.user?.locations || [],
    changeTimezoneOnly
  });
}
/**
 * Converts SRM date to moment
 * @param {string} date - YYYYMMDD
 * @param {string} [time] - HHmmss
 * @return {moment.Moment}
 */
export function srmDateToMoment(date, time) {
  if (!time) {
    return moment(date, SRM_DATE_FORMAT);
  }
  return moment(`${date} ${time}`, SRM_DATE_TIME_FORMAT);
}

/**
 * Converts moment to SRM date
 * @param {moment.Moment} date
 * @return {[string, string]} [date, time]
 */
export function momentToSrmDate(date) {
  return date.format("YYYYMMDD HHmmss").split(" ");
}

export function formattedSRMTimeValue(time) {
  if (!time) {
    return "00:00:00";
  }
  return `${time.substring(0, 2)}:${time.substring(2, 4)}:${time.substring(4)}`;
}

export const calculateDuration = (startDate, endDate) => {
  const value = endDate.clone().diff(moment(startDate.clone()));
  const time = Number(value);
  if (!isFinite(time) || time < 0) return;

  const isSameMonth = startDate.isSame(endDate, "month");
  let months, weeks, days, hours;
  if (isSameMonth) {
    months = endDate.diff(startDate, "months");
    const daysInMonth = endDate.diff(startDate, "days");
    weeks = Math.floor(daysInMonth / 7);
    days = daysInMonth % 7;
    hours = endDate.diff(startDate, "hours") % 24;
  } else {
    const years = endDate.diff(startDate, "years");
    months = endDate.diff(startDate.clone().add(years, "years"), "months");
    weeks = endDate.diff(
      startDate.clone().add(years, "years").add(months, "months"),
      "weeks"
    );
    days = endDate.diff(
      startDate
        .clone()
        .add(years, "years")
        .add(months, "months")
        .add(weeks, "weeks"),
      "days"
    );
    hours = endDate.diff(startDate, "hours") % 24;
  }

  return { months, weeks, days, hours };
};

export const LONG_DATE_TIME_FORMAT = "MMM D, YYYY - h:mm A";
export const FULL_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
export const FULL_DASHED_DATE_FORMAT = "YYYY-MM-DD";
export const AM_PM_TIME_FORMAT = "h:mm A";
export const LOCALE_DATETIME_FORMAT = `${moment
  .localeData(process.env.LOCALE)
  .longDateFormat("L")} h:mm A`;
export const RENTAL_BOARD_LAST_UPDATED_FORMAT = "YYYY/MM/DD h:mm A";
export const TIMETRACKER_FORMAT = "MM/DD/YY h:mm A";
export const TIMETRACKER_FORMAT_ERROR = `Please enter a valid date (${TIMETRACKER_FORMAT})`;
export const MONTH_DAY_YEAR_DATE_FORMAT = "MMM D, YYYY";
export const MONTH_YEAR_DATE_FORMAT = "MMM YYYY";
export const WEEK_FORMAT = "dddd D";
export const WEEKLY_TEXT_FORMAT = "dddd";
export const MONTHLY_DATE_TEXT_FORMAT = "MMMM D";

export const SRM_DATE_FORMAT = "YYYYMMDD";
export const SRM_TIME_FORMAT = "HHmmss";
export const SRM_DATE_TIME_FORMAT = "YYYY-MM-DD HHmmss";

export const FLASK_DATE_FORMAT = FULL_DASHED_DATE_FORMAT;

export const INVOICE_DATE_FORMAT = "DD/MM/YYYY";
export const END_OF_DAY = "235900";
export const DATE_PICKER_INPUT_DATE_FORMAT = "MMM DD, YYYY";

export const WORKORDER_DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss.SSS";
export const YEAR_TIME_FORMAT = "YYYY";
export const YEAR_MONTH_DAY_DATE_FORMAT = "YYYY/MM/DD";
